import React, { useMemo } from 'react'
import {
  Alert,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import ThroughputChart, { ThroughputSeries } from './ThroughputChart'
import { recordStateTiles } from './recordStatesModel'
import { emptyLaneMessage, idleRunView, liveTileHints } from './idleModel'
import {
  DEFICIT_COLOR,
  DEPTH_COLORS,
  describeDeficit,
  isDeficitStalled,
  LIMIT_SOURCE_LABELS,
  WINDOW_OPTIONS,
  formatAge,
  formatCadence,
  formatCompact,
  formatEta,
  formatExact,
  formatSaturation,
} from '../../types/autocoderThroughput'
import type {
  AutoCoderThroughputResponse,
  ThroughputDeficit,
  ThroughputQueueState,
  ThroughputRecordStates,
  ThroughputWindowKey,
} from '../../types/autocoderThroughput'

/**
 * ThroughputPanels — the whole AutoCoder throughput picture, rendered from one
 * payload.
 *
 * This is shared by BOTH surfaces that show it:
 *   - /admin/:customer/autocoder-throughput — every pipeline for a customer;
 *   - the Throughput tab of a single pipeline's status modal, where the same
 *     endpoint is served scoped to that pipeline id.
 *
 * They are the same panels on purpose. The user-facing view was a progress bar
 * and six counters while the admin view had lane saturation, queue depth and a
 * live token rate; the fix is one component, not a second implementation that
 * drifts. Which slice of work is being described is a property of the DATA
 * (response.pipelineId), never of the rendering.
 *
 * Everything the page cannot know precisely is labelled rather than smoothed
 * over: unrecognised model strings get their own muted lane, hand-tuned rate
 * limits are marked "≈", the coding rate names its proxy, and an ETA the server
 * has not earned renders as "—" rather than a plausible number.
 */

const STATE_COLORS: Record<string, 'info' | 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  running: 'info',
  completed: 'success',
  failed: 'error',
}

const StatTile: React.FC<{
  label: string
  value: string
  hint?: string
  emphasis?: boolean
}> = ({ label, value, hint, emphasis }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      height: '100%',
      borderColor: emphasis ? 'info.main' : 'divider',
      backgroundColor: emphasis ? 'action.hover' : undefined,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.2, mt: 0.5 }}>
      {value}
    </Typography>
    {hint && (
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {hint}
      </Typography>
    )}
  </Paper>
)

const SectionHeading: React.FC<{ title: string; note?: string }> = ({ title, note }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{title}</Typography>
    {note && (
      <Tooltip title={note}>
        <InfoIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
      </Tooltip>
    )}
  </Stack>
)

/**
 * RecordStatesPanel — lifetime records by state, for ONE pipeline.
 *
 * This is the fact the Import Status section owned and nothing else on this page
 * could tell: every other panel counts batches, or counts the current run. It is
 * labelled "lifetime" everywhere because it sits inches from BacklogPanel, which
 * counts the same pipeline over a different span — two true numbers that will
 * legitimately disagree, so the page has to say which is which.
 *
 * There is deliberately no progress bar and no percentage here. For a long-lived
 * pipeline the denominator grows on every ingest, so a "% done" slides backwards
 * when new data lands. Counts stay honest when the denominator moves.
 *
 * Renders nothing customer-wide: summing records by state across unrelated
 * pipelines is a number with no referent.
 */
const RecordStatesPanel: React.FC<{ states: ThroughputRecordStates }> = ({ states }) => {
  const tiles = recordStateTiles(states)
  if (!tiles) return null

  return (
    <Box sx={{ mb: 3 }}>
      <SectionHeading title="Records by state (lifetime)" note={states.note} />
      <Grid container spacing={2}>
        {tiles.map(tile => (
          <Grid item xs={6} sm={4} md={2} key={tile.label}>
            <StatTile
              label={tile.label}
              value={tile.value}
              hint={tile.hint}
              emphasis={tile.emphasis}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

/**
 * BacklogPanel — "how many things are in the queue, and how far through is it".
 *
 * Records first, because records are the unit an operator counts; batches are
 * shown second because they are the unit the ETA is actually measured in. The
 * ETA is always marked as an estimate and is "—" whenever the server declined
 * to produce one (fewer than three completed batches in the run).
 *
 * IDLE IS A STATE, NOT AN ABSENCE. When no run is in flight this panel used to
 * collapse to a single sentence, which meant a queue that had just finished
 * thousands of records looked identical to one that had never run — see
 * idleModel.ts for the incident that motivated the change. It now keeps showing
 * what the window completed, how long it has been quiet, and the lifetime tally
 * behind both.
 */
const BacklogPanel: React.FC<{ data: AutoCoderThroughputResponse }> = ({ data }) => {
  const backlog = data.backlog

  if (!backlog.active) {
    const idle = idleRunView(data)
    return (
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <SectionHeading
          title="Current run"
          note="No batches are pending or running. The figures below describe the selected window and the lifetime tally, so the panel still says what happened rather than going blank."
        />
        <Typography sx={{ fontWeight: 600, mb: idle.windowLine ? 1 : 0.5 }}>
          {idle.headline}
        </Typography>

        {idle.windowLine && (
          <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.3, mb: 1 }}>
            {idle.windowLine}
          </Typography>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {idle.idleForLabel && (
            <Chip size="small" variant="outlined" label={idle.idleForLabel} />
          )}
          {idle.lifetimeLine && (
            <Chip size="small" variant="outlined" label={idle.lifetimeLine} />
          )}
        </Stack>

        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {idle.basis}
        </Typography>
      </Paper>
    )
  }

  const percent = Math.max(0, Math.min(100, backlog.percentComplete))

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderColor: 'info.main' }}>
      <SectionHeading
        title="Current run"
        note="Batches enqueued at or after the oldest batch still in flight. Earlier runs are excluded, so the progress bar is about the work happening now."
      />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1, md: 3 }}
        alignItems={{ md: 'baseline' }}
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.2 }}>
          {formatExact(backlog.codedRecords)}
          <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '1rem' }}>
            {' / '}
            {formatExact(backlog.totalRecords)} records coded
          </Typography>
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          <strong>{formatExact(backlog.remainingRecords)}</strong> records still queued
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Est. time remaining <strong>{formatEta(backlog.etaSeconds)}</strong>
        </Typography>
      </Stack>

      <LinearProgress
        variant="determinate"
        color="info"
        value={percent}
        sx={{ height: 8, borderRadius: 1, mb: 1.5 }}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Chip size="small" label={`${percent.toFixed(percent < 10 ? 1 : 0)}% complete`} color="info" />
        <Chip size="small" variant="outlined" label={`${formatExact(backlog.totalBatches)} batches`} />
        <Chip
          size="small"
          variant={backlog.completedBatches > 0 ? 'filled' : 'outlined'}
          color="success"
          label={`completed: ${formatExact(backlog.completedBatches)}`}
        />
        <Chip
          size="small"
          variant={backlog.runningBatches > 0 ? 'filled' : 'outlined'}
          color="info"
          label={`running: ${formatExact(backlog.runningBatches)}`}
        />
        <Chip
          size="small"
          variant={backlog.pendingBatches > 0 ? 'filled' : 'outlined'}
          color="warning"
          label={`pending: ${formatExact(backlog.pendingBatches)}`}
        />
        {backlog.failedBatches > 0 && (
          <Chip size="small" color="error" label={`failed: ${formatExact(backlog.failedBatches)}`} />
        )}
      </Stack>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {backlog.etaBasis}
      </Typography>
    </Paper>
  )
}

/**
 * DeficitPanel (D4) — the RESERVOIR, rendered directly under the queue-depth
 * chart so the two read as one story: records pile up behind a closed gate
 * (deficit climbing, depth flat at zero), the gate opens (deficit converts into
 * depth), the queue drains.
 *
 * It exists because every other number on this page counts ENQUEUED work. On
 * 2026-07-27 a job held 2,474 uncoded records with zero batches for hours while
 * every readout said "queue empty". The loudest state this panel has to render
 * is therefore deficit > 0 WHILE queue = 0 — which is why both the headline
 * sentence and the border colour are chosen from BOTH numbers, never from the
 * deficit alone.
 *
 * The accent colour is deliberately its own: the reservoir is not more queue,
 * so it must not borrow the queue's orange/blue.
 */
const DeficitPanel: React.FC<{
  deficit: ThroughputDeficit
  pendingBatches: number
  runningBatches: number
}> = ({ deficit, pendingBatches, runningBatches }) => {
  const empty = deficit.records === 0
  // The one alarming combination: work exists, nothing is scheduled to do it,
  // and it is NOT the deliberate coding-off hold. Derived by the SAME predicate
  // the headline sentence uses (per pipeline, never from the customer-wide
  // queue counts), so colour and words can never tell different stories.
  const stalled = isDeficitStalled(deficit, pendingBatches, runningBatches)

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 3,
        borderColor: empty ? 'divider' : stalled ? 'error.main' : DEFICIT_COLOR,
        borderWidth: empty ? 1 : 2,
      }}
    >
      <SectionHeading title="Not in the queue (deficit)" note={deficit.note} />

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 3 }}
        alignItems={{ md: 'center' }}
        sx={{ mb: deficit.pipelines.length > 0 ? 2 : 0 }}
      >
        <Box sx={{ minWidth: 150 }}>
          <Typography
            sx={{
              fontSize: '2rem',
              fontWeight: 700,
              lineHeight: 1.1,
              color: empty ? 'text.disabled' : stalled ? 'error.main' : DEFICIT_COLOR,
            }}
          >
            {formatExact(deficit.records)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            uncoded records not in any live batch
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {describeDeficit(deficit, pendingBatches, runningBatches)}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={`queue now: ${formatExact(pendingBatches)} pending · ${formatExact(runningBatches)} running`}
            />
            {/* T19 (2026-08-12) retired the seal gate this used to split into
                two chips; coding is the only gate left. */}
            {deficit.heldByCodingOff > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`${formatExact(deficit.heldByCodingOff)} held — coding off`}
              />
            )}
            {/* Neither held nor coming: no gate moves a failed record and the
                sealer refuses to re-bill it, so it needs a person. */}
            {deficit.needsRetry > 0 && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`${formatExact(deficit.needsRetry)} failed — needs retry`}
              />
            )}
          </Stack>
        </Box>
      </Stack>

      {deficit.pipelines.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Pipeline</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell align="right">Live batches</TableCell>
                <TableCell>Why</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deficit.pipelines.map((p) => (
                <TableRow key={p.pipelineId} hover>
                  <TableCell>
                    {p.name}
                    {p.job && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {p.customer}/{p.job}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatExact(p.records)}</TableCell>
                  <TableCell align="right">{formatExact(p.liveBatches)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={
                        p.codingOff
                          ? 'warning'
                          : p.liveBatches > 0
                            ? 'info'
                            : 'error'
                      }
                      label={p.reasonLabel}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

/** The window selector. Shared so the two surfaces cannot offer different
 *  windows — the server owns the list, this just renders it. */
export const ThroughputWindowPicker: React.FC<{
  value: ThroughputWindowKey
  onChange: (value: ThroughputWindowKey) => void
}> = ({ value, onChange }) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    color="info"
    value={value}
    onChange={(_, v) => v && onChange(v as ThroughputWindowKey)}
  >
    {WINDOW_OPTIONS.map((opt) => (
      <ToggleButton key={opt.value} value={opt.value}>
        {opt.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
)

/** "Last 60 minutes · 1m buckets · auto-refresh every 30s · updated 18:29:27".
 *  The cadence is read from the payload, never assumed, because the server cuts
 *  it while work is in flight. */
export const ThroughputCaption: React.FC<{ data: AutoCoderThroughputResponse }> = ({ data }) => (
  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
    {data.window.label} ·{' '}
    {data.window.bucketSeconds >= 3600
      ? `${data.window.bucketSeconds / 3600}h`
      : `${data.window.bucketSeconds / 60}m`}{' '}
    buckets · auto-refresh every {formatCadence(data.window.pollSeconds)}
    {data.window.pollBoostedByActivity && ' while work is in flight'}
    {' · updated '}
    {new Date(data.generatedAt).toLocaleTimeString()}
  </Typography>
)

export interface ThroughputPanelsProps {
  data: AutoCoderThroughputResponse
  /**
   * "Most active pipelines" is a ranking ACROSS pipelines, so it is pure noise
   * inside a single pipeline's tab — one row, always itself. Off there, on for
   * the customer-wide overview.
   */
  showTopPipelines?: boolean
  /**
   * The "Coding queue" snapshot panel (state chips + by-model_version table).
   * The pipeline tab renders LaneQueueView above these panels — the SAME
   * table (autocoder_coding_batch), batch by batch, grouped by canonical lane
   * — so showing this panel there is the one queue told twice in two
   * vocabularies. Off there.
   *
   * On for the admin overview. Its hero is LlmLanesPanel, which reads
   * `llm_request` — every model call in flight, not just coding batches — so
   * this panel is the only breakdown of the CODING queue on that page, and
   * the two are different facts rather than one fact twice.
   */
  showQueueSnapshot?: boolean
}

const ThroughputPanels: React.FC<ThroughputPanelsProps> = ({
  data,
  showTopPipelines = true,
  showQueueSnapshot = true,
}) => {
  const laneSeries: ThroughputSeries[] = useMemo(
    () =>
      data.lanes.map((lane) => ({
        name: lane.label,
        muted: !lane.known,
        points: lane.buckets.map((b) => [new Date(b.t).getTime(), b.tokensPerMin] as [number, number]),
      })),
    [data],
  )

  const codingSeries: ThroughputSeries[] = useMemo(
    () => [
      {
        name: 'Records coded',
        points: data.coding.buckets.map((b) => [new Date(b.t).getTime(), b.records] as [number, number]),
      },
    ],
    [data],
  )

  // Queue depth shares the lane chart's x-axis but not its y-axis: depth is a
  // count of batches (tens), throughput is a rate (millions of tokens/min). A
  // second y-axis on one chart would flatten the depth line into the baseline,
  // so this is its own chart directly beneath, same window, same buckets.
  const depthSeries: ThroughputSeries[] = useMemo(
    () => [
      {
        name: 'Running',
        color: DEPTH_COLORS.running,
        points: data.queue.depth.map((d) => [new Date(d.t).getTime(), d.running] as [number, number]),
      },
      {
        name: 'Pending',
        color: DEPTH_COLORS.pending,
        points: data.queue.depth.map((d) => [new Date(d.t).getTime(), d.pending] as [number, number]),
      },
    ],
    [data],
  )

  const queueByState = useMemo(() => {
    const map: Record<string, ThroughputQueueState> = {}
    for (const s of data.queue.states) map[s.state] = s
    return map
  }, [data])

  const unknownLanes = data.lanes.filter((l) => !l.known)

  // The tiles are trailing rates, so they hit zero the moment a run lands. The
  // hints carry the context that keeps a zero readable (idleModel.ts).
  const liveHints = liveTileHints(data)

  return (
    <>
      {/* ── Summary strip ───────────────────────────────────────────────── */}
      {/* The headline is a TRAILING rate, not the last complete bucket: on a
          7-day window that bucket is 2h wide and reads 0 for most of a live
          run. Long windows stay useful for shape below. */}
      {/* No "Uncoded, not queued" tile here: the deficit is DeficitPanel's
          story below (born of the same 2026-07-27 incident), and the same
          number told twice with different hints read as two different facts. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Tokens / min"
            value={formatCompact(data.live.tokensPerMin)}
            hint={liveHints.tokensHint}
            emphasis={!liveHints.idle}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Requests / min"
            value={formatCompact(data.live.requestsPerMin)}
            hint={liveHints.requestsHint}
          />
        </Grid>
        {/* "Batches in flight", NOT "pending batches".
            Since B11 a controller batch is BORN `running` — a batch is in
            flight from the moment it exists, and being born outside `pending`
            is what stops a pre-cutover replica claiming it (see
            store_autocoder_controller.go). So a tile reading `pending` alone
            shows a hard zero during even the busiest drain, which is how it
            read while 25 batches were mid-flight on 2026-08-14. Pending is
            still disclosed in the hint, because a NON-zero pending count now
            means something specific: legacy phase-less rows awaiting adoption. */}
        <Grid item xs={6} md={3}>
          <StatTile
            label="Batches in flight"
            value={formatCompact((queueByState.running?.count ?? 0) + (queueByState.pending?.count ?? 0))}
            hint={
              data.queue.oldestPendingAgeSeconds !== null
                ? `${formatCompact(queueByState.pending?.count ?? 0)} pending · oldest ${formatAge(data.queue.oldestPendingAgeSeconds)}`
                : `${formatCompact(queueByState.running?.count ?? 0)} running · 0 pending`
            }
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Records coded"
            value={formatCompact(data.coding.totalRecords)}
            hint={`${formatCompact(data.coding.totalBatches)} batches in window`}
          />
        </Grid>
      </Grid>

      {/* ── Backlog + progress of the run happening now ──────────────────── */}
      <BacklogPanel data={data} />

      {/* ── Panel 1: lane activity ──────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <SectionHeading
          title="Lane activity"
          note="Tokens per minute per model lane, from autocoder token-usage logs. Buckets are chosen by the server for the selected window."
        />
        <ThroughputChart
          series={laneSeries}
          valueFormatter={formatCompact}
          yAxisName="tokens/min"
          emptyMessage={emptyLaneMessage(data)}
        />

        {unknownLanes.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {unknownLanes.length === 1 ? '1 logged model string does' : `${unknownLanes.length} logged model strings do`}
            {' '}not map to a catalog lane and {unknownLanes.length === 1 ? 'is' : 'are'} shown in
            {' '}their own bucket rather than dropped:{' '}
            {unknownLanes.map((l) => l.label).join(', ')}.
          </Alert>
        )}

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lane</TableCell>
                <TableCell align="right">Tokens</TableCell>
                <TableCell align="right">Requests</TableCell>
                <TableCell align="right">Peak tokens/min</TableCell>
                <TableCell align="right">Seed TPM</TableCell>
                <TableCell align="right">Peak vs seed</TableCell>
                <TableCell>Limit basis</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.lanes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ color: 'text.disabled' }}>
                    No lane activity in this window.
                  </TableCell>
                </TableRow>
              )}
              {data.lanes.map((lane) => (
                <TableRow key={lane.laneId} hover>
                  <TableCell>
                    <Tooltip
                      title={lane.rawModels.length ? `Logged as: ${lane.rawModels.join(', ')}` : ''}
                    >
                      <Box
                        component="span"
                        sx={{ color: lane.known ? 'text.primary' : 'text.disabled', fontWeight: lane.known ? 600 : 400 }}
                      >
                        {lane.label}
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{formatCompact(lane.totalTokens)}</TableCell>
                  <TableCell align="right">{formatCompact(lane.totalRequests)}</TableCell>
                  <TableCell align="right">{formatCompact(lane.peakTokensPerMin)}</TableCell>
                  <TableCell align="right">
                    {lane.limitTpm > 0 ? formatCompact(lane.limitTpm) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {formatSaturation(lane.peakTpmPercent, lane.limitSource)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={lane.limitSource === 'arm-seed' ? 'info' : 'default'}
                      label={LIMIT_SOURCE_LABELS[lane.limitSource]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          Seed limits come from the model catalog. Only ARM-calibrated lanes are checked against real
          Azure deployment limits; hand-tuned figures are shown with “≈” and should not be read as
          precise saturation.
          {data.pipelineId && (
            <>
              {' '}Lanes are shared: a seed limit is the whole deployment’s, not this pipeline’s
              share of it, so saturation here is this pipeline’s contribution to a ceiling other
              work also draws on.
            </>
          )}
        </Typography>
      </Paper>

      {/* ── Queue depth — backlog next to throughput ─────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
        <SectionHeading
          title="Queue depth"
          note="Batches waiting and in flight, on the same x-axis as lane activity above — backlog against the throughput chewing through it."
        />
        <ThroughputChart
          series={depthSeries}
          valueFormatter={formatCompact}
          yAxisName="batches"
          height={200}
          emptyMessage="No batches waiting or running in this window"
        />
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {data.queue.depthNote}
        </Typography>
      </Paper>

      {/* ── Lifetime records by state (per-pipeline only) ───────────────────
          Sits directly above the deficit because the two answer adjacent
          questions: how the pipeline's records are distributed over all time,
          then which of them are not in the queue right now. */}
      <RecordStatesPanel states={data.recordStates} />

      {/* ── The reservoir behind the gate, paired with the depth chart ───── */}
      <DeficitPanel
        deficit={data.deficit}
        pendingBatches={queueByState.pending?.count ?? 0}
        runningBatches={queueByState.running?.count ?? 0}
      />

      <Grid container spacing={3}>
        {/* ── Panel 2: queue state ── suppressed where LaneQueueView already
            shows this queue batch-by-batch (see showQueueSnapshot). */}
        {showQueueSnapshot && (
        <Grid item xs={12} lg={5}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <SectionHeading title="Coding queue" note="Point-in-time snapshot of autocoder_coding_batch — not windowed." />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {data.queue.states.map((s) => (
                <Chip
                  key={s.state}
                  color={s.known ? STATE_COLORS[s.state] || 'default' : 'default'}
                  variant={s.count > 0 ? 'filled' : 'outlined'}
                  label={`${s.state}: ${formatCompact(s.count)}${s.known ? '' : ' (unknown state)'}`}
                  size="small"
                />
              ))}
            </Stack>

            <Typography variant="body2" sx={{ mb: 2 }}>
              Oldest pending batch: <strong>{formatAge(data.queue.oldestPendingAgeSeconds)}</strong>
            </Typography>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>model_version</TableCell>
                    <TableCell>Lane</TableCell>
                    <TableCell align="right">Pending</TableCell>
                    <TableCell align="right">Running</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.queue.byModelVersion.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: 'text.disabled' }}>
                        No coding batches.
                      </TableCell>
                    </TableRow>
                  )}
                  {data.queue.byModelVersion.map((lane) => (
                    <TableRow key={lane.modelVersion} hover>
                      <TableCell>{lane.modelVersion || '(blank)'}</TableCell>
                      <TableCell sx={{ color: lane.resolved ? 'text.primary' : 'text.disabled' }}>
                        {lane.resolved ? lane.laneId : 'unresolved'}
                      </TableCell>
                      <TableCell align="right">{formatCompact(lane.byState.pending ?? 0)}</TableCell>
                      <TableCell align="right">{formatCompact(lane.byState.running ?? 0)}</TableCell>
                      <TableCell align="right">{formatCompact(lane.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" sx={{ mt: 2 }}>
              {data.queue.laneVocabularyNote}
            </Alert>
          </Paper>
        </Grid>
        )}

        {/* ── Panel 3: coding progress ────────────────────────────────── */}
        <Grid item xs={12} lg={showQueueSnapshot ? 7 : 12}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <SectionHeading title="Coding progress" note={data.coding.source} />
            <ThroughputChart
              series={codingSeries}
              variant="bar"
              valueFormatter={formatCompact}
              yAxisName="records"
              height={220}
              emptyMessage="No batches completed in this window"
            />

            {showTopPipelines && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Most active pipelines
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pipeline</TableCell>
                        <TableCell>Customer / job</TableCell>
                        <TableCell align="right">Batches</TableCell>
                        <TableCell align="right">Records</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.coding.topPipelines.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ color: 'text.disabled' }}>
                            No pipelines completed coding work in this window.
                          </TableCell>
                        </TableRow>
                      )}
                      {data.coding.topPipelines.map((p) => (
                        <TableRow key={p.pipelineId} hover>
                          <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={p.pipelineId}>
                              <span>{p.name}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{[p.customer, p.job].filter(Boolean).join(' / ') || '—'}</TableCell>
                          <TableCell align="right">{formatCompact(p.batches)}</TableCell>
                          <TableCell align="right">{formatCompact(p.records)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1.5 }}>
              {data.coding.source}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </>
  )
}

export default ThroughputPanels
